import { FRAMEWORK_INDUSTRY_TYPES } from '../Constants/frameworkGatedMenu';

// All possible user types
export const USER_TYPES = {
  // Admin types
  SUPER_ADMIN: 'Super Admin - Admin',
  TECH_ADMIN: 'Tech Admin - Admin',
  TECH_MEMBER: 'Tech Admin - Member',
  
  // Consultant types
  CONSULTANT_ADMIN: 'Consultant - Admin',
  CONSULTANT_MEMBER: 'Consultant - Member',
  
  // Auditor types
  AUDITOR_ADMIN: 'Auditor - Admin',
  AUDITOR_MEMBER: 'Auditor - Member',
  
  // User types
  USER_ADMIN: 'User - Admin',
  USER_MANAGEMENT: 'User - Management',
  USER_VENDOR: 'User - Vendor',
  USER_MEMBER: 'User - Member',
  USER_GUEST: 'User - Guest'
};

export const isTrinityPortal = () => process.env.REACT_APP_PORTAL_MODE === 'trinity';

export const trinityProductOrigin = () =>
  process.env.REACT_APP_PRODUCT_ORIGIN || 'https://dev.zavior.app';

// Get current user type from localStorage
export const getUserType = () => localStorage.getItem("userType");

/** Super Admin or Tech Admin — same rule as IAM `IsTrinityAllowed`. */
export const isTrinityAllowedRole = (role) => {
  const value = (role || '').trim();
  return value === USER_TYPES.SUPER_ADMIN || value === USER_TYPES.TECH_ADMIN;
};

export const isSuperAdminOnly = () => isTrinityAllowedRole(getUserType());

// User - Member / Guest / Vendor: policy & document surfaces are read-only
// (no extract/assign/update/ingest; Ask AI browse Approved only).
export const isRestrictedEndUser = () => {
  const userType = getUserType();
  return [
    USER_TYPES.USER_MEMBER,
    USER_TYPES.USER_GUEST,
    USER_TYPES.USER_VENDOR,
  ].includes(userType);
};

/**
 * Roles that live in Focus Mode only (user decision, 2026-10-21): the restricted end users
 * (User - Member / Guest / Vendor) plus the regulator roles Zavvy blocks. They never see the
 * Pro toggle and any /adminlist URL sends them back to /view/focus.
 */
export const FOCUS_ONLY_ROLES = [
  USER_TYPES.USER_MEMBER,
  USER_TYPES.USER_GUEST,
  USER_TYPES.USER_VENDOR,
  'Regulator - Admin',
  'Regulator - Member',
];
export const focusOnlyRole = (role) => FOCUS_ONLY_ROLES.includes((role || '').trim());
export const isFocusOnlyUser = () => focusOnlyRole(getUserType());

// Check if user is any type of admin
export const isAdminUser = () => {
  const userType = getUserType();
  const adminTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.USER_ADMIN
  ];
  return adminTypes.includes(userType);
};

// Check if user has admin/consultant privileges
export const adminUserTypes = () => {
  const userType = getUserType();
  const allowedUserTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.TECH_MEMBER,
  ];
  return allowedUserTypes.includes(userType);
};

// Check if user has management level access or higher
export const hasManagementAccess = () => {
  const userType = getUserType();
  const managementTypes = [
    USER_TYPES.USER_ADMIN,
    USER_TYPES.USER_MANAGEMENT
  ];
  return managementTypes.includes(userType);
}; 

// Check if user has tech admin access
export const hasTechAdminAccess = () => {
  const userType = getUserType();
  const techAdminTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.TECH_MEMBER
  ];
  return techAdminTypes.includes(userType);
};

// Check if user is strictly Super Admin or Tech Admin (excludes Tech Member)
export const hasSuperOrTechAdminOnly = () => {
  const userType = getUserType();
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN
  ];
  return allowedTypes.includes(userType);
};

/**
 * Knowledge Library actions (global model knowledge_library). Mirrors the IAM L2 seed
 * 2026-10-23-SEED_KNOWLEDGE_LIBRARY_L2_PERMISSIONS.sql: Super Admin and Tech Admin have
 * create, read, update and delete; Tech Admin - Member has create, read and update. IAM
 * enforces the same rows on every call.
 */
export const getKnowledgeLibraryAccess = () => {
  const userType = getUserType();
  const full = [USER_TYPES.SUPER_ADMIN, USER_TYPES.TECH_ADMIN].includes(userType);
  const member = userType === USER_TYPES.TECH_MEMBER;
  return { read: full || member, create: full || member, update: full || member, delete: full };
};

/**
 * Library Sync now and sync status: the AI backend's library routes allow Super Admin, Tech
 * Admin - Admin and Tech Admin - Member (ai_backend_service app/routes/v1/library.py).
 */
export const canSyncKnowledgeLibrary = () => hasTechAdminAccess();

// Check if user can access resource defaults
export const canAccessResourceDefaults = () => {
  const userType = getUserType();
  // Based on backend database permissions in model_access_l2
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,     // Full access to global and org defaults
    USER_TYPES.TECH_ADMIN,      // Full access to global and org defaults  
    USER_TYPES.TECH_MEMBER,     // Full access to global and org defaults
    USER_TYPES.USER_ADMIN,      // Read-only access to org defaults
    USER_TYPES.CONSULTANT_ADMIN, // Full access to org defaults for assigned clients
    USER_TYPES.CONSULTANT_MEMBER // Read-only access to org defaults for assigned clients
    // USER_MANAGEMENT excluded - backend doesn't grant access
  ];
  return allowedTypes.includes(userType);
};

// Legacy alias for backward compatibility
export const canAccessSystemDefaultFields = canAccessResourceDefaults;

// Check if user can manage global resource defaults
export const canManageGlobalDefaults = () => {
  const userType = getUserType();
  // Only Tech Admin and Super Admin can manage global defaults
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.TECH_MEMBER
  ];
  return allowedTypes.includes(userType);
};

// Check if user can manage organization-specific defaults
export const canManageOrgDefaults = () => {
  const userType = getUserType();
  // Based on backend database permissions - only these roles have CREATE/UPDATE/DELETE on resource_defaults
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,     // Full CRUD access
    USER_TYPES.TECH_ADMIN,      // Full CRUD access
    USER_TYPES.TECH_MEMBER,     // Full CRUD access (no delete on global)
    USER_TYPES.CONSULTANT_ADMIN // Full CRUD access to org defaults for assigned clients
    // USER_ADMIN excluded - backend only grants READ access
    // USER_MANAGEMENT excluded - backend doesn't grant CRUD access
    // CONSULTANT_MEMBER excluded - read-only access only
  ];
  return allowedTypes.includes(userType);
};

// User Review Setup create actions on My Organization (policy, training,
// governance, hardware). Separate from resource defaults: User - Admin has
// CRUD on these models. User - Member and the other end-user roles do not.
export const canManageUserReviewSetup = () => {
  const userType = getUserType();
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.TECH_MEMBER,
    USER_TYPES.CONSULTANT_ADMIN,
    USER_TYPES.USER_ADMIN,
  ];
  return allowedTypes.includes(userType);
};

// Check if user can curate/publish the Trust Center (create/update/delete —
// branding edits, layout reorder, share-link create/rotate/revoke).
// Mirrors the model_access_l2 seed for 'trust_center' — every other role
// gets read-only access to the internal report (menu_trust_center visibility
// is separate, see hasMenuPermission('menu_trust_center')).
export const canManageTrustCenter = () => {
  const userType = getUserType();
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.USER_ADMIN,
    USER_TYPES.CONSULTANT_ADMIN,
  ];
  return allowedTypes.includes(userType);
};

// Check if user can manage File Sharing / External URLs (create short links,
// set expiry/password, expire links, attach legacy links). Everyone else
// (Auditor, User Member/Vendor/Guest, etc.) only ever sees the resolved
// document — never the short-link management UI.
export const canManageFileSharing = () => {
  const userType = getUserType();
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.USER_ADMIN,
    USER_TYPES.CONSULTANT_ADMIN,
  ];
  return allowedTypes.includes(userType);
};

// Check if user can use AI Policy Mapping for Controls (read + create + update)
// Mirrors the model_access_l2 seed for ai_policy_mapping_for_controls.
export const hasAIPolicyMappingAccess = () => {
  const userType = getUserType();
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.TECH_MEMBER,
    USER_TYPES.USER_ADMIN,
    USER_TYPES.USER_MANAGEMENT,
    USER_TYPES.CONSULTANT_ADMIN,
  ];
  return allowedTypes.includes(userType);
};

// Check if user can apply AI-suggested mappings (update permission)
export const canApplyAIPolicyMapping = () => hasAIPolicyMappingAccess();

// Check if user can only read AI mapping results (no create/update)
export const canReadAIPolicyMapping = () => {
  const userType = getUserType();
  const readOnlyTypes = [
    USER_TYPES.AUDITOR_ADMIN,
    USER_TYPES.AUDITOR_MEMBER,
    USER_TYPES.CONSULTANT_MEMBER,
    USER_TYPES.USER_MEMBER,
  ];
  return hasAIPolicyMappingAccess() || readOnlyTypes.includes(userType);
};

// Check if user can run or re-run AI scoring
export const hasAIScoringAccess = () => {
  const userType = getUserType();
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.TECH_MEMBER,
    USER_TYPES.USER_ADMIN,
    USER_TYPES.CONSULTANT_ADMIN,
    USER_TYPES.CONSULTANT_MEMBER,
  ];
  return allowedTypes.includes(userType);
};

// Check if user has consultant access
export const hasConsultantAccess = () => {
  const userType = getUserType();
  const consultantTypes = [
    USER_TYPES.CONSULTANT_ADMIN,
    USER_TYPES.CONSULTANT_MEMBER
  ];
  return consultantTypes.includes(userType);
};

// Soft-delete a Project. Org-scoped; User - Admin can remove their org's
// projects the same way Tech Admin / Consultant already can.
export const canDeleteProject = () => (
  hasTechAdminAccess() ||
  hasConsultantAccess() ||
  getUserType() === USER_TYPES.USER_ADMIN
);

/** Tech Admin or Consultant Admin — folder/zip pack import in UI */
export const canImportTemplatePack = () => {
  if (hasTechAdminAccess()) return true;
  return getUserType() === USER_TYPES.CONSULTANT_ADMIN;
};



export const riskUserTypes = () => {
  const userType = localStorage.getItem("userType");
  const allowedUserTypes = ['Super Admin - Admin', 'Tech Admin - Admin', 'Consultant - Admin'];
  const isAdminTypeUser = allowedUserTypes.includes(userType);
  return isAdminTypeUser;
};

// Risk Expert (run, accept suggestions). The risk pages make every risk field inline-editable for
// the roles canAccessRiskAssessment lets in, so the Risk Expert follows that list minus the
// read-only auditors: Super Admin, Tech Admin and Member, User Admin, User Management, Consultant
// Admin and Member. Members, vendors and guests never reach the pages.
export const hasRiskExpertAccess = () => canAccessRiskAssessment() && !hasAuditorAccess();

// Check if user can access Risk Assessment
// Allowed: Tech Admin roles, User Admin, User Management, Consultants, Auditors
// NOT Allowed: User Member, User Vendor, User Guest
export const canAccessRiskAssessment = () => {
  const userType = getUserType();
  const allowedTypes = [
    USER_TYPES.SUPER_ADMIN,
    USER_TYPES.TECH_ADMIN,
    USER_TYPES.TECH_MEMBER,
    USER_TYPES.USER_ADMIN,
    USER_TYPES.USER_MANAGEMENT,
    USER_TYPES.CONSULTANT_ADMIN,
    USER_TYPES.CONSULTANT_MEMBER,
    USER_TYPES.AUDITOR_ADMIN,
    USER_TYPES.AUDITOR_MEMBER
  ];
  return allowedTypes.includes(userType);
};

// Check if user has auditor access
export const hasAuditorAccess = () => {
  const userType = getUserType();
  const auditorTypes = [
    USER_TYPES.AUDITOR_ADMIN,
    USER_TYPES.AUDITOR_MEMBER
  ];
  return auditorTypes.includes(userType);
};

// Check if user can access the Audit module
// Allowed: Auditors (Admin/Member) + Tech Admin (Admin/Member)
export const hasAuditModuleAccess = () => {
  return hasAuditorAccess() || hasTechAdminAccess();
};

// =============================================================================
// MENU PERMISSION FUNCTIONS
// These functions check model_access_l2 permissions stored in localStorage
// Permissions are loaded during login and stored as menuPermissions
// =============================================================================

/**
 * Get all menu permissions from localStorage
 * @returns {Object} Map of modelName -> boolean (true if access granted)
 */
export const getMenuPermissions = () => {
  try {
    return JSON.parse(localStorage.getItem('menuPermissions') || '{}');
  } catch {
    return {};
  }
};

/**
 * Check if user has permission to access a specific menu item
 * @param {string} modelName - The model name associated with the menu item (e.g., 'menu_dashboard', 'control')
 * @returns {boolean} True if user has READ permission for the model
 */
/**
 * Roles that keep the legacy "no permissions loaded yet means allowed" fallback. Everyone
 * else gets nothing from an empty map, so a failed permission fetch never widens a
 * member's menu.
 */
const MENU_FALLBACK_ROLES = [
  USER_TYPES.SUPER_ADMIN,
  USER_TYPES.TECH_ADMIN,
  USER_TYPES.TECH_MEMBER,
  USER_TYPES.USER_ADMIN,
  USER_TYPES.USER_MANAGEMENT,
  USER_TYPES.CONSULTANT_ADMIN,
];

export const hasMenuPermission = (modelName) => {
  if (!modelName) return true; // No model = no restriction
  const menuPermissions = getMenuPermissions();
  // No permissions loaded yet: admin-tier roles keep the legacy allow, other roles get nothing.
  if (Object.keys(menuPermissions).length === 0) return MENU_FALLBACK_ROLES.includes((getUserType() || '').trim());
  return menuPermissions[modelName] === true;
};

/**
 * Check if menu permissions are loaded (non-empty)
 * @returns {boolean} True if menu permissions have been loaded from login
 */
export const areMenuPermissionsLoaded = () => {
  const permissions = getMenuPermissions();
  return Object.keys(permissions).length > 0;
};

// =============================================================================
// FRAMEWORK INDUSTRY TYPE GATING
//
// Two separate caches drive the 3-condition gate shown in the sidebar:
//
//   Condition 1 (org gate):   orgIndustryTypes — which industry types the org's
//                              projects cover. Populated by loadOrgIndustryTypes().
//
//   Condition 2 (admin map):  frameworkMappings — Map<menuModel, Set<industryType>>
//                              built from menu_access_l3 rows where subject is an
//                              industry type string (not a profile UUID). Populated
//                              by loadFrameworkMappings().
//
//   Condition 3 (role perm):  hasMenuPermission(menuModel) — standard Menu Access
//                              check from localStorage.menuPermissions.
//
// Both caches are cleared on org/account switch via clearOrgIndustryTypesCache()
// and clearFrameworkMappingsCache() in AuthContext.js.
//
// Primary data source: the backend-enriched login/switch response now returns
// `framework_menu_mappings` and `org_industry_types` alongside `menu_permissions`.
// seedPermissionContext() stores these in localStorage and seeds the in-memory
// caches immediately, so the sidebar never needs to make a separate API call for
// first-render.  The API fallback in loadFrameworkMappings() is kept for
// Tech Admin manual-refresh flows.
// =============================================================================

let _orgIndustryTypesCache = null; // Set<string> | null

// ---------------------------------------------------------------------------
// Internal helpers: seed in-memory caches from localStorage
// ---------------------------------------------------------------------------

const _seedOrgIndustryTypesFromStorage = () => {
  try {
    const raw = localStorage.getItem('orgIndustryTypes');
    if (!raw) return;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) {
      _orgIndustryTypesCache = new Set(arr);
    }
  } catch {
    // ignore malformed data
  }
};

const _seedFrameworkMappingsFromStorage = () => {
  try {
    const raw = localStorage.getItem('frameworkMenuMappings');
    if (!raw) return;
    const obj = JSON.parse(raw);
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      const map = new Map();
      Object.entries(obj).forEach(([model, types]) => {
        if (Array.isArray(types)) map.set(model, new Set(types));
      });
      _frameworkMappingsCache = map;
    }
  } catch {
    // ignore malformed data
  }
};

// ---------------------------------------------------------------------------
// seedPermissionContext — called at login and org/account switch
// ---------------------------------------------------------------------------

/**
 * Store and activate the full permission context returned by the backend-enriched
 * login / switch-account response.
 *
 * Persists `framework_menu_mappings` and `org_industry_types` to localStorage
 * so a page refresh does not require another API call.  Also seeds the
 * in-memory caches immediately so the sidebar renders on the first paint
 * without waiting for any extra fetch.
 *
 * @param {Object|null}  frameworkMenuMappings  { menuModel: [industryType, …] }
 * @param {string[]|null} orgIndustryTypes       unique industry-type strings
 */
export const seedPermissionContext = (frameworkMenuMappings, orgIndustryTypes) => {
  // Only persist and seed framework mappings when the payload contains at least one
  // entry. An empty {} most likely means ACS has not yet been updated to accept
  // non-admin JWTs — the IAM service silently returns {} rather than the real data.
  // By leaving the localStorage key absent (and _frameworkMappingsCache as null),
  // loadFrameworkMappings() can still reach the direct API-call path on first
  // sidebar render and pick up the correct data once ACS is updated.
  const hasMappings =
    frameworkMenuMappings &&
    typeof frameworkMenuMappings === 'object' &&
    !Array.isArray(frameworkMenuMappings) &&
    Object.keys(frameworkMenuMappings).length > 0;

  if (hasMappings) {
    localStorage.setItem('frameworkMenuMappings', JSON.stringify(frameworkMenuMappings));
    _seedFrameworkMappingsFromStorage(); // populate in-memory cache immediately
  } else {
    localStorage.removeItem('frameworkMenuMappings');
    // _frameworkMappingsCache intentionally left as null — loadFrameworkMappings()
    // will still attempt the /menu-permissions-framework API call on first render.
  }

  if (Array.isArray(orgIndustryTypes)) {
    localStorage.setItem('orgIndustryTypes', JSON.stringify(orgIndustryTypes));
  } else {
    localStorage.removeItem('orgIndustryTypes');
  }
  _seedOrgIndustryTypesFromStorage();
};

/**
 * Clear the cached org industry-type set.
 * Call this whenever the active org or account changes.
 */
export const clearOrgIndustryTypesCache = () => {
  _orgIndustryTypesCache = null;
  localStorage.removeItem('orgIndustryTypes');
};

/**
 * Load and cache the set of Framework Industry Types covered by the org's
 * projects.  For each project, reads the linked framework's
 * `framework_industry_type` (jsonb array) and unions all values.
 *
 * Tries localStorage (seeded at login/switch) before making an API call.
 *
 * @param {string} orgId - The current organisation ID
 * @returns {Promise<Set<string>>} Resolved set of industry-type strings
 */
export const loadOrgIndustryTypes = async (orgId) => {
  if (_orgIndustryTypesCache !== null) return _orgIndustryTypesCache;

  // Seed from login/switch data stored in localStorage
  _seedOrgIndustryTypesFromStorage();
  if (_orgIndustryTypesCache !== null) return _orgIndustryTypesCache;

  try {
    const { GetProjectData } = await import('../Api/Project');
    const response = await GetProjectData(orgId);
    const projects = Array.isArray(response?.data) ? response.data :
                     Array.isArray(response) ? response : [];

    const industryTypes = new Set();
    projects.forEach(project => {
      const frameworkIndustryType = project?.framework?.framework_industry_type ||
                                    project?.framework_industry_type;
      if (Array.isArray(frameworkIndustryType)) {
        frameworkIndustryType.forEach(t => { if (t) industryTypes.add(t); });
      } else if (typeof frameworkIndustryType === 'string') {
        try {
          const parsed = JSON.parse(frameworkIndustryType);
          if (Array.isArray(parsed)) parsed.forEach(t => { if (t) industryTypes.add(t); });
        } catch {
          if (frameworkIndustryType) industryTypes.add(frameworkIndustryType);
        }
      }
    });

    _orgIndustryTypesCache = industryTypes;
    return industryTypes;
  } catch (error) {
    console.warn('Could not load org industry types for gating:', error);
    _orgIndustryTypesCache = new Set();
    return _orgIndustryTypesCache;
  }
};

/**
 * Returns the cached org industry-type set synchronously.
 * Returns null if the cache has not been populated yet.
 */
export const getOrgIndustryTypesSync = () => _orgIndustryTypesCache;

/**
 * Force-rebuild org industry types from the org's projects (no SwitchAccount).
 *
 * Used after project create/delete so sidebar / Focus Mode gating updates for
 * the same org without requiring a re-login. Clears the in-memory + localStorage
 * cache, reloads via GetProjectData, persists the result, and notifies listeners.
 * Next sign-in / account switch still overwrites with server org_industry_types.
 *
 * @param {string} [orgId] - defaults to localStorage.organizationId
 * @returns {Promise<boolean>} true if the cache was refreshed
 */
export const refreshOrgIndustryTypesFromProjects = async (orgId) => {
  const resolvedOrgId = orgId || localStorage.getItem('organizationId');
  if (!resolvedOrgId) return false;
  try {
    clearOrgIndustryTypesCache();
    const types = await loadOrgIndustryTypes(resolvedOrgId);
    const arr = Array.from(types || []);
    localStorage.setItem('orgIndustryTypes', JSON.stringify(arr));
    window.dispatchEvent(new CustomEvent('sidebar:orgSwitched'));
    return true;
  } catch (error) {
    console.warn('refreshOrgIndustryTypesFromProjects failed:', error);
    return false;
  }
};

// =============================================================================
// FRAMEWORK MAPPINGS CACHE
// Map<menuModel, Set<industryType>> — built from menu_access_l3 rows where
// subject is an industry type string (not a profile UUID).
// This is condition 2 of the 3-condition framework gate.
// =============================================================================

let _frameworkMappingsCache = null; // Map<menuModel, Set<industryType>> | null
let _frameworkMappingsInFlight = null; // Promise<Map> | null — dedup parallel callers

/**
 * Clear the cached framework mappings.
 * Call this whenever the active org or account changes so the new org's
 * Menu Access (Framework) configuration is fetched fresh.
 */
export const clearFrameworkMappingsCache = () => {
  _frameworkMappingsCache = null;
  _frameworkMappingsInFlight = null;
  localStorage.removeItem('frameworkMenuMappings');
};

/**
 * Refresh the current user's permission context in place — WITHOUT a full
 * re-login, redirect, or clearing the selected project.
 *
 * Used after first-time onboarding completes: a project was just created, so
 * the org's industry-type coverage (and therefore sidebar gating) changed, but
 * the context was seeded at login when the org had none. This re-issues the
 * JWT and re-seeds menu permissions + framework gating from the switch-account
 * response, then notifies the sidebar / Focus Mode to re-render.
 *
 * Unlike AuthContext.switchAccount this does NOT touch `projectID`, `mode`, or
 * navigate, so it is safe to call from anywhere. Failures are swallowed — a
 * refresh hiccup must never block onboarding completion.
 *
 * @param {string} [accountId] - defaults to localStorage.accountId / userLogindata.AccountID
 * @returns {Promise<boolean>} true if the context was refreshed
 */
export const refreshPermissionContext = async (accountId) => {
  let resolvedAccountId = accountId || localStorage.getItem('accountId');
  if (!resolvedAccountId) {
    try {
      resolvedAccountId = JSON.parse(localStorage.getItem('userLogindata') || '{}')?.AccountID || null;
    } catch {
      resolvedAccountId = null;
    }
  }
  if (!resolvedAccountId) return false;

  try {
    const { SwitchAccount } = await import('../Api/switchAccount');
    const response = await SwitchAccount(resolvedAccountId);
    const data = response?.data;
    if (!data) return false;

    if (data.access_token) {
      localStorage.setItem('jwtToken', data.access_token);
    }
    if (data.menu_permissions) {
      localStorage.setItem('menuPermissions', JSON.stringify(data.menu_permissions));
    } else {
      localStorage.removeItem('menuPermissions');
    }

    // Drop stale caches, then re-seed from the fresh payload (does not touch projectID).
    clearOrgIndustryTypesCache();
    clearFrameworkMappingsCache();
    seedPermissionContext(
      data.framework_menu_mappings ?? null,
      data.org_industry_types ?? null
    );

    // Tell the sidebar (and Focus Mode) to re-read the refreshed context.
    window.dispatchEvent(new CustomEvent('sidebar:orgSwitched'));
    return true;
  } catch (error) {
    console.warn('refreshPermissionContext failed:', error);
    return false;
  }
};

/**
 * Load and cache the framework mappings.
 *
 * Tries localStorage (seeded at login/switch) before hitting the network.
 * Uses in-flight promise deduplication so React Strict Mode double-mounts
 * and parallel callers only ever trigger one network round-trip.
 *
 * Builds a Map<menuModel, Set<industryType>> where:
 *   object  = industryType  (e.g. 'Cyber Security')
 *   models  = menuModel     (e.g. 'menu_domain_names')
 *
 * @returns {Promise<Map<string, Set<string>>>}
 */
export const loadFrameworkMappings = async () => {
  if (_frameworkMappingsCache !== null) return _frameworkMappingsCache;

  // Seed from login/switch data stored in localStorage
  _seedFrameworkMappingsFromStorage();
  if (_frameworkMappingsCache !== null) return _frameworkMappingsCache;

  // In-flight deduplication — only one network call at a time
  if (_frameworkMappingsInFlight) return _frameworkMappingsInFlight;

  _frameworkMappingsInFlight = (async () => {
    try {
      const { GetAllFrameworkMappings } = await import('../Api/MenuPermissions');
      const response = await GetAllFrameworkMappings({ silent: true });
      const rows = Array.isArray(response?.data) ? response.data :
                   Array.isArray(response) ? response : [];
      const map = new Map();
      rows
        .filter(r => !r.is_deleted && r.action === 'read' && r.object)
        .forEach(r => {
          // r.object = industryType, r.models = menuModel
          if (!map.has(r.models)) map.set(r.models, new Set());
          map.get(r.models).add(r.object);
        });
      // Only permanently cache when we got real data. An empty result likely means
      // ACS still rejects the JWT (old code) — leave the cache null so the next
      // sidebar mount can retry once the service is updated.
      if (map.size > 0) {
        _frameworkMappingsCache = map;
      }
      return map;
    } catch (error) {
      console.warn('Could not load framework mappings for gating:', error);
      // Do NOT permanently cache on failure — leave _frameworkMappingsCache as null
      // so the next sidebar mount can retry (e.g. after ACS is updated).
      return new Map();
    } finally {
      _frameworkMappingsInFlight = null;
    }
  })();

  return _frameworkMappingsInFlight;
};

/**
 * Returns the cached framework mappings synchronously.
 * Returns null if the cache has not been populated yet.
 */
export const getFrameworkMappingsSync = () => _frameworkMappingsCache;

/**
 * Check conditions 1+2 of the 3-condition framework gate.
 *
 *   Condition 1 (org gate): org has a project covering at least one of the
 *                            menu item's mapped industry types.
 *   Condition 2 (admin map): the menu item is ticked in Menu Access (Framework)
 *                             for that industry type (entry exists in frameworkMappings).
 *
 * IMPORTANT: This function does NOT check condition 3 (hasMenuPermission for role).
 * The caller (filterByAccess in NewSideBarNavigation.js) is responsible for that.
 *
 * Fail-closed on empty coverage: a LOADED-but-empty orgIndustryTypes Set means
 * the org has no project carrying a framework_industry_type. In that case we
 * GATE (hide) framework-mapped menus — a user with no project should see only
 * base menus until one exists. A null Set (not yet loaded) is likewise
 * fail-closed to avoid a load-time flash. Base menus are unaffected: they have
 * no frameworkMappings entry, so the caller never runs this gate on them.
 *
 * @param {string}              menuModel        - e.g. 'menu_domain_names'
 * @param {Set<string>|null}    orgIndustryTypes - Org's covered industry types
 * @param {Map<string,Set>|null} frameworkMappings - Admin-configured menu→type map
 * @returns {boolean} true only if conditions 1+2 pass (org covers a mapped type)
 */
export const isFrameworkGatePassed = (menuModel, orgIndustryTypes, frameworkMappings) => {
  if (!frameworkMappings || !orgIndustryTypes) return false;
  const requiredTypes = frameworkMappings.get(menuModel);
  if (!requiredTypes || requiredTypes.size === 0) return false; // not in Framework tab
  if (orgIndustryTypes.size === 0) return false; // no project coverage → gate closed (hide framework menus until a project exists)
  for (const t of requiredTypes) {
    if (orgIndustryTypes.has(t)) return true; // org project matches mapped type
  }
  return false;
};

/**
 * Project-Industry framework gate for a Focus Mode category.
 *
 * Unlike the sidebar's isFrameworkGatePassed (which reads the admin-configured
 * frameworkMappings table), Focus Mode stores the required industry types
 * directly on the category's `permissions` JSONB as `framework_industry` entries.
 *
 * Logic:
 *   - No `framework_industry` perms on the category  → gate skipped (visible to all orgs)
 *   - Has them → the org must cover AT LEAST ONE (OR within the selected types)
 *
 * This is layer 1 of the 3-layer gate; the caller still applies role/group (layer 2/3).
 *
 * @param {Array<{permission_type:string,permission_value:string,can_view:boolean}>} categoryPermissions
 * @param {Set<string>|null} orgIndustryTypes - union of the org projects' framework_industry_type
 * @returns {boolean} true when the framework gate passes
 */
export const hasCategoryFrameworkGatePassed = (categoryPermissions, orgIndustryTypes) => {
  const frameworkPerms = (categoryPermissions || []).filter(
    p => p.permission_type === 'framework_industry' && p.can_view
  );
  if (frameworkPerms.length === 0) return true; // no gate configured → visible to all orgs
  if (!orgIndustryTypes || orgIndustryTypes.size === 0) return false;
  return frameworkPerms.some(p => orgIndustryTypes.has(p.permission_value));
};